
import {
  defineComponent,
  PropType,
  ref,
  SetupContext,
  watch,
  computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import Calendar from 'primevue/calendar';
import Button from 'primevue/button';

// Interface pour étendre le type Calendar avec la propriété overlayVisible
interface CalendarWithOverlay extends InstanceType<typeof Calendar> {
  overlayVisible: boolean;
}

export default defineComponent({
  name: 'BaseCalendar',

  components: {
    Calendar,
    Button,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: String as PropType<string>,
      required: true,
    },

    title: {
      type: String,
      required: false,
      default: '',
    },

    tooltipDesc: {
      type: String,
      required: false,
      default: '',
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    hideOnSelect: {
      type: Boolean,
      required: false,
      default: false,
    },

    type: {
      type: String as PropType<'date' | 'datetime'>,
      required: false,
      default: 'datetime',
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: string; disabled?: boolean; hideOnSelect?: boolean; type?: 'date' | 'datetime' }, { emit }: SetupContext) {
    const { t } = useI18n();
    // Référence typée pour le composant Calendar avec la propriété overlayVisible
    const calendar = ref<CalendarWithOverlay | null>(null);
    const time = ref(props.modelValue);

    // Détermine si le calendrier doit afficher l'heure ou non
    const isDateTime = computed(() => props.type === 'datetime');

    watch([time], () => emit('update:modelValue', time.value));

    const onDateSelect = () => {
      // Fermer le calendrier après la sélection d'une date seulement si hideOnSelect est true
      if (calendar.value && props.hideOnSelect) {
        // Utiliser la propriété overlayVisible directement sur la référence
        calendar.value.overlayVisible = false;
      }
    };

    // Fonction pour fermer le calendrier lorsque le bouton Confirmer est cliqué
    const closeCalendar = () => {
      if (calendar.value) {
        calendar.value.overlayVisible = false;
      }
    };

    return {
      t,
      time,
      calendar,
      onDateSelect,
      isDateTime,
      closeCalendar,
    };
  },
});
